import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    getInvoicingPreflight, listInvoiceRuns, createInvoiceRun, deleteInvoiceRun, refreshInvoiceRunPreview,
    pushInvoiceRun, getInvoiceRun, voidInvoiceRunLine, retryInvoiceRunLinePdf,
    sendInvoiceRunLineEmails, markInvoiceRunLinesSent,
    InvoicePreflightResponse, InvoiceRunSummary, InvoicePreview, InvoicePreviewProject,
    InvoiceRunLine, SubcontractorSplitRow,
} from "../services/api";

const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "8px 20px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
};
const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "8px 18px", cursor: "pointer", fontSize: "13px",
};
const dangerBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#C00000", border: "1px solid #C00000", borderRadius: "4px",
    padding: "6px 14px", cursor: "pointer", fontSize: "12px", fontWeight: "bold",
};
const smallBtnStyle: React.CSSProperties = {
    backgroundColor: "#5C5D5E", color: "white", border: "none", borderRadius: "4px",
    padding: "5px 12px", cursor: "pointer", fontSize: "11px",
};
const sectionStyle: React.CSSProperties = {
    backgroundColor: "white", border: "1px solid #e0e0e0", borderRadius: "6px",
    padding: "16px 20px", marginBottom: "20px",
};
const thStyle: React.CSSProperties = {
    textAlign: "left", padding: "8px 10px", fontSize: "12px",
    color: "white", backgroundColor: "#5C5D5E", whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
    textAlign: "left", padding: "8px 10px", fontSize: "13px",
    borderBottom: "1px solid #f0f0f0",
};

const money = (v: number | null | undefined): string => {
    if (v === null || v === undefined) return "—";
    const neg = v < 0;
    const s = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return neg ? `($${s})` : `$${s}`;
};

const STATUS_LABELS: Record<string, string> = {
    pushed: "Pushed (Draft)", pdf_ready: "PDF Ready", emailed: "Emailed",
    marked_sent: "Marked Sent", voided: "Voided", failed: "Failed",
};
const STATUS_COLORS: Record<string, string> = {
    pushed: "#5C5D5E", pdf_ready: "#0070C0", emailed: "#2E7D32",
    marked_sent: "#2E7D32", voided: "#999", failed: "#C00000",
};

function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}

// 5a: recompute a project's line items/total after dropping whatever time
// entries are currently excluded on the client -- pure arithmetic (sum
// hours x rate), same math the server already validated matches Zoho's own
// rollup, so no round trip is needed just to preview an exclusion.
function recomputeProject(project: InvoicePreviewProject, excluded: Set<string>) {
    const lineItems = project.line_items.map(li => {
        const keptEntries = li.entries.filter(e => !e.time_entry_id || !excluded.has(e.time_entry_id));
        const hours = keptEntries.reduce((sum, e) => sum + e.hours, 0);
        const amount = Math.round(hours * li.rate * 100) / 100;
        return { ...li, hours: Math.round(hours * 100) / 100, amount, entries: keptEntries };
    }).filter(li => li.entries.length > 0);
    const total = Math.round(lineItems.reduce((sum, li) => sum + li.amount, 0) * 100) / 100;
    return { lineItems, total };
}

const InvoiceAutomation: React.FC = () => {
    const [step, setStep] = useState<"start" | "preview" | "summary">("start");
    const [error, setError] = useState<string | null>(null);

    // Step 1/2: pre-flight + recent runs
    const [billUpTo, setBillUpTo] = useState<string>(todayIso());
    const [preflightLoading, setPreflightLoading] = useState(false);
    const [preflight, setPreflight] = useState<InvoicePreflightResponse | null>(null);
    const [recentRuns, setRecentRuns] = useState<InvoiceRunSummary[]>([]);
    const [deletingRunId, setDeletingRunId] = useState<number | null>(null);

    // Step 3-6: run + preview/approval
    const [runId, setRunId] = useState<number | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [preview, setPreview] = useState<InvoicePreview | null>(null);
    const [approvedIds, setApprovedIds] = useState<Set<number>>(new Set());
    const [excludedByProject, setExcludedByProject] = useState<Record<number, Set<string>>>({});
    const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());
    const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());
    const [pushing, setPushing] = useState(false);

    // BACKLOG.md #19 (2026-09-10): manual per-subcontractor split of a
    // pooled "Amplify Subcontractor" line item. Purely local state until
    // push, same as excludedByProject above -- keyed project_id -> pooled
    // line item label -> the admin's saved rows. splitEditorKey/
    // splitDraftRows hold whichever single split is currently being
    // edited (`${project_id}::${label}`), one at a time.
    const [subcontractorSplits, setSubcontractorSplits] = useState<Record<number, Record<string, SubcontractorSplitRow[]>>>({});
    const [splitEditorKey, setSplitEditorKey] = useState<string | null>(null);
    const [splitDraftRows, setSplitDraftRows] = useState<{ name: string; hours: string }[]>([]);

    // Step 7-11: summary/run
    const [runLines, setRunLines] = useState<InvoiceRunLine[]>([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    // BACKLOG.md #27b: which bulk action is currently running, so each
    // button can show its own in-progress text (Push already does this
    // via the separate `pushing` flag; Send Email/Mark as Sent only ever
    // greyed out with no text change before this).
    const [bulkAction, setBulkAction] = useState<"email" | "marking" | null>(null);
    const [bulkResultMsg, setBulkResultMsg] = useState<string | null>(null);
    const [voidingLineId, setVoidingLineId] = useState<number | null>(null);
    const [retryingLineId, setRetryingLineId] = useState<number | null>(null);

    const loadRecentRuns = useCallback(async () => {
        try {
            const data = await listInvoiceRuns();
            setRecentRuns(data.runs);
        } catch (e) {
            console.error("Failed to load recent invoice runs", e);
        }
    }, []);

    useEffect(() => { loadRecentRuns(); }, [loadRecentRuns]);

    const runPreflight = async () => {
        setPreflightLoading(true);
        setError(null);
        try {
            const data = await getInvoicingPreflight(billUpTo);
            setPreflight(data);
        } catch (e) {
            console.error("Failed to run pre-flight", e);
            setError("Failed to run pre-flight checks. Please try again.");
        } finally {
            setPreflightLoading(false);
        }
    };

    const startRun = async () => {
        setError(null);
        try {
            const data = await createInvoiceRun(billUpTo);
            setRunId(data.run.id);
            setPreview(data.preview);
            setApprovedIds(new Set(data.run.approved_project_ids));
            setExcludedByProject({});
            setExpandedProjects(new Set());
            setExpandedLines(new Set());
            setSubcontractorSplits({});
            setSplitEditorKey(null);
            setSplitDraftRows([]);
            setStep("preview");
        } catch (e: any) {
            console.error("Failed to start invoice run", e);
            // BACKLOG.md #27d: surfaces the real reason (most commonly "a
            // run is already in progress") instead of a generic message --
            // same convention as handleRetryPdf/ProjectFinancials.tsx.
            setError(e?.response?.data?.detail || "Failed to start the invoice run. Please try again.");
        }
    };

    // BACKLOG.md #27d/#33: a run can be deleted once every line is voided
    // (or never actually reached Zoho) -- the backend is authoritative
    // (400s otherwise), this button is just hidden/disabled for the
    // common case so it's not an inviting dead end.
    const handleDeleteRun = async (run: InvoiceRunSummary) => {
        setError(null);
        setDeletingRunId(run.id);
        try {
            await deleteInvoiceRun(run.id);
            await loadRecentRuns();
        } catch (e: any) {
            console.error("Failed to delete invoice run", e);
            setError(e?.response?.data?.detail || "Failed to delete this run. Please try again.");
        } finally {
            setDeletingRunId(null);
        }
    };

    const resumeRun = async (run: InvoiceRunSummary) => {
        setError(null);
        setRunId(run.id);
        setApprovedIds(new Set(run.approved_project_ids));
        try {
            const summary = await getInvoiceRun(run.id);
            if (summary.lines.length > 0) {
                setRunLines(summary.lines);
                setStep("summary");
                return;
            }
        } catch (e) {
            console.error("Failed to load run", e);
        }
        setPreviewLoading(true);
        try {
            const data = await refreshInvoiceRunPreview(run.id);
            setPreview(data.preview);
            setStep("preview");
        } catch (e) {
            console.error("Failed to load run preview", e);
            setError("Failed to resume this run. Please try again.");
        } finally {
            setPreviewLoading(false);
        }
    };

    const refreshPreview = async () => {
        if (!runId) return;
        setPreviewLoading(true);
        setError(null);
        try {
            const data = await refreshInvoiceRunPreview(runId);
            setPreview(data.preview);
        } catch (e) {
            console.error("Failed to refresh preview", e);
            setError("Failed to refresh. Please try again.");
        } finally {
            setPreviewLoading(false);
        }
    };

    const toggleApproved = (projectId: number) => {
        setApprovedIds(prev => {
            const next = new Set(prev);
            if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
            return next;
        });
    };

    const toggleExpandedProject = (projectId: number) => {
        setExpandedProjects(prev => {
            const next = new Set(prev);
            if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
            return next;
        });
    };

    const toggleExpandedLine = (key: string) => {
        setExpandedLines(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleEntryExcluded = (projectId: number, timeEntryId: string | null) => {
        if (!timeEntryId) return;
        setExcludedByProject(prev => {
            const current = new Set(prev[projectId] || []);
            if (current.has(timeEntryId)) current.delete(timeEntryId); else current.add(timeEntryId);
            return { ...prev, [projectId]: current };
        });
    };

    // BACKLOG.md #19: opens the split editor pre-filled with either the
    // existing saved split for this pooled line, or a single draft row
    // seeded with its full pooled hours (Pete's spec: "editor pre-filled
    // with one row, allows adding rows that sum back to the merged total").
    const openSplitEditor = (projectId: number, label: string, pooledHours: number) => {
        const existing = subcontractorSplits[projectId]?.[label];
        setSplitDraftRows(
            existing && existing.length > 0
                ? existing.map(r => ({ name: r.name, hours: String(r.hours) }))
                : [{ name: "", hours: pooledHours.toFixed(2) }]
        );
        setSplitEditorKey(`${projectId}::${label}`);
    };

    const cancelSplitEditor = () => {
        setSplitEditorKey(null);
        setSplitDraftRows([]);
    };

    const addSplitRow = () => setSplitDraftRows(prev => [...prev, { name: "", hours: "" }]);

    const removeSplitRow = (idx: number) => setSplitDraftRows(prev => prev.filter((_, i) => i !== idx));

    const updateSplitRow = (idx: number, field: "name" | "hours", value: string) => {
        setSplitDraftRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
    };

    const splitDraftTotalHours = useMemo(
        () => Math.round(splitDraftRows.reduce((sum, r) => sum + (parseFloat(r.hours) || 0), 0) * 100) / 100,
        [splitDraftRows],
    );

    const saveSplitEditor = (projectId: number, label: string) => {
        const rows: SubcontractorSplitRow[] = splitDraftRows
            .filter(r => r.name.trim() && parseFloat(r.hours) > 0)
            .map(r => ({ name: r.name.trim(), hours: Math.round(parseFloat(r.hours) * 100) / 100 }));
        setSubcontractorSplits(prev => ({
            ...prev,
            [projectId]: { ...(prev[projectId] || {}), [label]: rows },
        }));
        cancelSplitEditor();
    };

    const clearSplit = (projectId: number, label: string) => {
        setSubcontractorSplits(prev => {
            const projectSplits = { ...(prev[projectId] || {}) };
            delete projectSplits[label];
            return { ...prev, [projectId]: projectSplits };
        });
    };

    const computedRows = useMemo(() => {
        if (!preview) return [];
        return preview.projects.map(p => {
            const excluded = excludedByProject[p.project_id] || new Set<string>();
            const { lineItems, total } = recomputeProject(p, excluded);
            return { ...p, line_items: lineItems, total };
        });
    }, [preview, excludedByProject]);

    const computedRunTotal = useMemo(
        () => Math.round(computedRows.reduce((sum, r) => sum + r.total, 0) * 100) / 100,
        [computedRows],
    );

    const handlePush = async () => {
        if (!runId) return;
        setPushing(true);
        setError(null);
        try {
            const exclusions: Record<string, string[]> = {};
            Object.entries(excludedByProject).forEach(([pid, ids]) => {
                if (ids.size > 0) exclusions[pid] = Array.from(ids);
            });
            // BACKLOG.md #19: only send projects that actually have a
            // non-empty saved split -- a project with no key here pushes
            // its pooled line as a single "Amplify Subcontractor" line,
            // same as before this feature existed.
            const subcontractorSplitsPayload: Record<string, Record<string, SubcontractorSplitRow[]>> = {};
            Object.entries(subcontractorSplits).forEach(([pid, byLabel]) => {
                const nonEmpty = Object.fromEntries(Object.entries(byLabel).filter(([, rows]) => rows.length > 0));
                if (Object.keys(nonEmpty).length > 0) subcontractorSplitsPayload[pid] = nonEmpty;
            });
            // 2026-09-10, Pete's explicit request: checking a project's box
            // already means "approve and push it" -- there's no separate
            // Approve step anymore (see BACKLOG.md #20). project_ids is
            // persisted as the run's approved set server-side by this same
            // call, then pushed immediately.
            const result = await pushInvoiceRun(runId, Array.from(approvedIds), exclusions, subcontractorSplitsPayload);
            setRunLines(result.lines);
            setStep("summary");
        } catch (e: any) {
            console.error("Failed to push invoice run", e);
            // Surface the real backend reason (e.g. a per-run validation
            // error) instead of a generic message that hides what actually
            // went wrong -- this exact gap is what made BACKLOG.md #20 hard
            // to diagnose the first time it happened.
            const detail = e?.response?.data?.detail;
            setError(typeof detail === "string" && detail ? detail : "Failed to push invoices to Zoho. Please try again.");
        } finally {
            setPushing(false);
        }
    };

    const refreshSummary = useCallback(async () => {
        if (!runId) return;
        setSummaryLoading(true);
        try {
            const data = await getInvoiceRun(runId);
            setRunLines(data.lines);
        } catch (e) {
            console.error("Failed to refresh run summary", e);
        } finally {
            setSummaryLoading(false);
        }
    }, [runId]);

    const toggleLineSelected = (lineId: number) => {
        setSelectedLineIds(prev => {
            const next = new Set(prev);
            if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
            return next;
        });
    };

    const handleVoid = async (lineId: number) => {
        if (!runId) return;
        setVoidingLineId(lineId);
        try {
            await voidInvoiceRunLine(runId, lineId);
            await refreshSummary();
        } catch (e: any) {
            console.error("Failed to void invoice", e);
            setBulkResultMsg(e?.response?.data?.detail || "Failed to void this invoice.");
        } finally {
            setVoidingLineId(null);
        }
    };

    const handleRetryPdf = async (lineId: number) => {
        if (!runId) return;
        setRetryingLineId(lineId);
        try {
            await retryInvoiceRunLinePdf(runId, lineId);
            await refreshSummary();
        } catch (e: any) {
            console.error("Failed to retry PDF", e);
            setBulkResultMsg(e?.response?.data?.detail || "Failed to retry PDF for this invoice.");
        } finally {
            setRetryingLineId(null);
        }
    };

    const handleBulkSendEmail = async () => {
        if (!runId || selectedLineIds.size === 0) return;
        // 2026-10-05: "emailed" lines are selectable again (Mark as Sent
        // needs them -- the v61 gate requires "emailed" first), so Send
        // Email must drop them itself rather than rely on the checkbox
        // being hidden (the v56 double-send fix, 11098). The backend
        // still refuses a re-send too; this just keeps a stray click from
        // even attempting it or leaving a misleading error on the row.
        const alreadyEmailedIds = new Set(
            runLines.filter(l => selectedLineIds.has(l.id) && l.status === "emailed").map(l => l.id)
        );
        const emailIds = Array.from(selectedLineIds).filter(id => !alreadyEmailedIds.has(id));
        if (emailIds.length === 0) {
            setBulkResultMsg("Those invoices have already been emailed -- nothing to send. Use Mark as Sent instead.");
            return;
        }
        setBulkBusy(true);
        setBulkAction("email");
        setBulkResultMsg(null);
        try {
            const result = await sendInvoiceRunLineEmails(runId, emailIds);
            const sent = result.outcomes.filter(o => o.sent).length;
            const failed = result.outcomes.length - sent;
            const skippedNote = alreadyEmailedIds.size > 0
                ? ` Skipped ${alreadyEmailedIds.size} already-emailed invoice${alreadyEmailedIds.size !== 1 ? "s" : ""}.`
                : "";
            setBulkResultMsg(`Sent ${sent} email${sent !== 1 ? "s" : ""}${failed ? `, ${failed} failed/skipped (see status column).` : "."}${skippedNote}`);
            // 2026-09-15 fix: invoice 11098 went out twice because a
            // successful send left its checkbox checked -- if the same
            // selection is still checked at the next "Send Email" click
            // (this batch, or a later one), those already-"emailed" lines
            // get resubmitted. The backend now refuses to actually
            // re-send (see send_run_line_email's status guard), but
            // clearing the selection here means a stray click can't even
            // attempt it, and Pete has to deliberately re-check anything
            // he actually wants to send again.
            setSelectedLineIds(new Set());
            await refreshSummary();
        } catch (e) {
            console.error("Failed to send invoice emails", e);
            setBulkResultMsg("Failed to send emails. Please try again.");
        } finally {
            setBulkBusy(false);
            setBulkAction(null);
        }
    };

    const handleBulkMarkSent = async () => {
        if (!runId || selectedLineIds.size === 0) return;
        // BACKLOG.md #27e: a project with no invoice_email_to on file gets
        // a confirmation pop-up every time (Pete's choice over a one-time
        // per-project flag, so there's always a conscious in-the-moment
        // confirmation) -- projects WITH an email on file are instead
        // hard-gated server-side (must actually reach "emailed" first, see
        // mark_run_line_sent's guard) and need no client-side prompt.
        const selectedLines = runLines.filter(l => selectedLineIds.has(l.id));
        const noEmailLines = selectedLines.filter(l => !l.project_has_invoice_email);
        if (noEmailLines.length > 0) {
            const names = Array.from(new Set(noEmailLines.map(l => l.project_name || "this project"))).join(", ");
            const confirmed = window.confirm(
                `${names} ${noEmailLines.length === 1 ? "has" : "have"} no email address on file in the app -- ` +
                `confirm you've sent ${noEmailLines.length === 1 ? "this invoice" : "these invoices"} another way before marking ` +
                `as sent.`
            );
            if (!confirmed) return;
        }
        setBulkBusy(true);
        setBulkAction("marking");
        setBulkResultMsg(null);
        try {
            let result = await markInvoiceRunLinesSent(runId, Array.from(selectedLineIds));

            // 2026-10-02: a line can come back needing confirmation rather
            // than a plain ok/fail -- Zoho already shows it out of Draft
            // (sent another way: an older run from before this app's
            // email-gate existed, or a one-off manual send in Zoho).
            // Confirm with Pete, then resubmit just those line_ids forced
            // through -- this never re-touches Zoho, it only records
            // locally what Zoho already shows.
            //
            // 2026-10-02 (v79): split into two sub-cases. Most of the time
            // Zoho's status check succeeded and just shows the invoice out
            // of Draft (zoho_status set) -- Pete is confirming what Zoho
            // itself reported. But sometimes Zoho won't even let the app
            // read the invoice's status at all (check_error set instead --
            // found on invoice 11102, Zoho error code 57, cause never
            // pinned down) -- there Pete is vouching for it himself (e.g.
            // he already checked Zoho directly), not confirming something
            // the app determined, so the dialog says so plainly.
            const needsConfirmation = result.outcomes.filter(o => o.needs_confirmation);
            if (needsConfirmation.length > 0) {
                const knownSent = needsConfirmation.filter(o => o.zoho_status);
                const checkFailed = needsConfirmation.filter(o => !o.zoho_status);
                const confirmedIds: number[] = [];

                if (knownSent.length > 0) {
                    const names = knownSent
                        .map(o => runLines.find(l => l.id === o.line_id)?.invoice_number || `line ${o.line_id}`);
                    const confirmed = window.confirm(
                        knownSent.length === 1
                            ? `Invoice ${names[0]} has already been Marked Sent in Zoho, confirm that it has been sent.`
                            : `Invoices ${names.join(", ")} have already been Marked Sent in Zoho, confirm that they have been sent.`
                    );
                    if (confirmed) confirmedIds.push(...knownSent.map(o => o.line_id));
                }

                if (checkFailed.length > 0) {
                    const names = checkFailed
                        .map(o => runLines.find(l => l.id === o.line_id)?.invoice_number || `line ${o.line_id}`);
                    const errs = Array.from(new Set(checkFailed.map(o => o.check_error).filter(Boolean))).join("; ");
                    const confirmed = window.confirm(
                        (checkFailed.length === 1
                            ? `Could not check invoice ${names[0]}'s current status in Zoho (${errs}). `
                            : `Could not check invoices ${names.join(", ")}'s current status in Zoho (${errs}). `) +
                        `If you've already confirmed ${checkFailed.length === 1 ? "it's" : "they've"} been sent, ` +
                        `confirm to mark ${checkFailed.length === 1 ? "it" : "them"} as sent here.`
                    );
                    if (confirmed) confirmedIds.push(...checkFailed.map(o => o.line_id));
                }

                if (confirmedIds.length > 0) {
                    const retryResult = await markInvoiceRunLinesSent(runId, confirmedIds, confirmedIds);
                    const byId = new Map(retryResult.outcomes.map(o => [o.line_id, o]));
                    result = {
                        outcomes: result.outcomes.map(o => byId.get(o.line_id) || o),
                    };
                }
            }

            const ok = result.outcomes.filter(o => o.ok).length;
            const failed = result.outcomes.length - ok;
            setBulkResultMsg(`Marked ${ok} invoice${ok !== 1 ? "s" : ""} as sent${failed ? `, ${failed} failed (see status column).` : "."}`);
            // BACKLOG.md #27g: same fix as Send Email above (#25) -- clear
            // the selection after a successful bulk action so the
            // just-processed lines can't be accidentally resubmitted.
            setSelectedLineIds(new Set());
            await refreshSummary();
        } catch (e) {
            console.error("Failed to mark invoices as sent", e);
            setBulkResultMsg("Failed to mark as sent. Please try again.");
        } finally {
            setBulkBusy(false);
            setBulkAction(null);
        }
    };

    return (
        <div>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 6px 0", fontSize: "22px" }}>T&amp;M Invoice Automation</h1>
            <p style={{ color: "#666", marginTop: 0, marginBottom: "20px", fontSize: "13px" }}>
                Automates the monthly Zoho Books T&amp;M invoicing flow. Fixed Price invoicing stays fully manual in Zoho.
            </p>
            {error && <div style={{ color: "#C00000", fontSize: "13px", marginBottom: "14px" }}>{error}</div>}

            {step === "start" && (
                <>
                    <div style={sectionStyle}>
                        <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 10px 0" }}>Start a New Run</h2>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
                            <label style={{ fontSize: "13px", color: "#333" }}>Bill up to:</label>
                            <input
                                type="date" value={billUpTo}
                                onChange={e => setBillUpTo(e.target.value)}
                                style={{ padding: "6px 10px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px" }}
                            />
                            <button style={secondaryBtnStyle} onClick={runPreflight} disabled={preflightLoading}>
                                {preflightLoading ? "Checking…" : "Run Pre-flight Check"}
                            </button>
                        </div>

                        {preflight && (
                            <div style={{ marginTop: "14px" }}>
                                <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
                                    {preflight.coverage_gaps.length === 0
                                        ? "No timesheet coverage gaps found for the period containing this date."
                                        : `${preflight.coverage_gaps.length} timesheet coverage gap(s) found for the period containing this date -- informational only, you can continue anyway:`}
                                </div>
                                {preflight.coverage_gaps.length > 0 && (
                                    <ul style={{ fontSize: "12px", color: "#7F6000", marginTop: 0, marginBottom: "12px" }}>
                                        {preflight.coverage_gaps.map((g, i) => (
                                            <li key={i}>{g.project_name} — {g.user_name}: {g.issue} ({g.period_start} – {g.period_end})</li>
                                        ))}
                                    </ul>
                                )}
                                {preflight.projects.some(p => p.blocked) && (
                                    <div style={{ fontSize: "12px", color: "#C00000", marginBottom: "12px" }}>
                                        {preflight.projects.filter(p => p.blocked).length} project(s) have Data Quality issues and will be
                                        shown but not selectable for invoicing until fixed:
                                        <ul style={{ marginTop: "4px", marginBottom: 0 }}>
                                            {preflight.projects.filter(p => p.blocked).map(p => (
                                                <li key={p.project_id}>{p.project_name} — {p.blocked_reasons.join("; ")}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <button style={primaryBtnStyle} onClick={startRun}>Start Run</button>
                            </div>
                        )}
                    </div>

                    <div style={sectionStyle}>
                        <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: "0 0 10px 0" }}>Recent Runs</h2>
                        {recentRuns.length === 0 ? (
                            <div style={{ fontSize: "13px", color: "#999" }}>No runs yet.</div>
                        ) : (
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Bill Up To</th>
                                        <th style={thStyle}>Created</th>
                                        <th style={thStyle}>Approved Projects</th>
                                        <th style={thStyle}>Status</th>
                                        <th style={thStyle}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentRuns.map(run => (
                                        <tr key={run.id}>
                                            <td style={tdStyle}>{run.bill_up_to_date}</td>
                                            <td style={tdStyle}>{run.created_at ? new Date(run.created_at).toLocaleString() : "—"}</td>
                                            <td style={tdStyle}>{run.approved_project_ids.length}</td>
                                            <td style={tdStyle}>
                                                {/* BACKLOG.md #27d: Complete once every line is marked_sent/voided --
                                                    Resume becomes View (the summary screen is already effectively
                                                    read-only for a Complete run, since every line's checkbox is
                                                    already hidden once it's marked_sent/voided). */}
                                                <span style={{ color: run.is_complete ? "#2E7D32" : "#0070C0", fontWeight: "bold" }}>
                                                    {run.is_complete ? "Complete" : "In Progress"}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                                                <button style={smallBtnStyle} onClick={() => resumeRun(run)}>
                                                    {run.is_complete ? "View" : "Resume"}
                                                </button>
                                                {/* 2026-10-02 fix: was run.line_count === 0, which meant a run
                                                    could never be deleted once it had pushed anything, even
                                                    after every line was voided -- matches the backend's
                                                    can_delete (zero lines, or every line voided; never true if
                                                    any line is genuinely marked_sent). */}
                                                {run.can_delete && (
                                                    <button
                                                        style={{ ...dangerBtnStyle, marginLeft: "6px", padding: "5px 10px", fontSize: "11px" }}
                                                        disabled={deletingRunId === run.id}
                                                        onClick={() => handleDeleteRun(run)}
                                                    >
                                                        {deletingRunId === run.id ? "Deleting…" : "Delete"}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}

            {step === "preview" && (
                <div style={sectionStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: 0 }}>
                            Preview &amp; Approve — Bill up to {preview?.bill_up_to}
                        </h2>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <button style={secondaryBtnStyle} onClick={refreshPreview} disabled={previewLoading}>
                                {previewLoading ? "Refreshing…" : "Refresh"}
                            </button>
                            <button style={secondaryBtnStyle} onClick={() => setStep("start")}>Back</button>
                        </div>
                    </div>
                    <p style={{ fontSize: "12px", color: "#666", marginTop: 0 }}>
                        Click a project row to expand its line items; click a line item to expand its individual timesheet
                        lines and exclude any from this run. Red rows have Data Quality issues and can't be approved. Purple
                        rows would push the project over budget.
                    </p>

                    {preview?.revenue_type_warning && (
                        // BACKLOG.md #28: surfaced here, before push, so a stale/invalid
                        // Revenue Type config value can be caught and fixed instead of
                        // silently falling back on every single invoice in this run.
                        <div style={{
                            fontSize: "12px", color: "#7F6000", backgroundColor: "#FFF9DB",
                            border: "1px solid #E6D488", borderRadius: "4px",
                            padding: "8px 12px", marginBottom: "12px",
                        }}>
                            Warning: {preview.revenue_type_warning}
                        </div>
                    )}

                    {!preview || computedRows.length === 0 ? (
                        <div style={{ fontSize: "13px", color: "#999", padding: "12px 0" }}>
                            No unbilled T&amp;M entries found for any in-scope project as of this date.
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}></th>
                                        <th style={thStyle}>Project</th>
                                        <th style={thStyle}>Customer</th>
                                        <th style={thStyle}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {computedRows.map(p => {
                                        const rowBg = p.blocked ? "#FDECEA" : p.over_budget ? "#F3E8FD" : "transparent";
                                        const nameColor = p.blocked ? "#C00000" : p.over_budget ? "#6A1B9A" : "#333";
                                        return (
                                            <React.Fragment key={p.project_id}>
                                                <tr style={{ backgroundColor: rowBg }}>
                                                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                                                        {!p.blocked && (
                                                            <input
                                                                type="checkbox"
                                                                checked={approvedIds.has(p.project_id)}
                                                                onChange={() => toggleApproved(p.project_id)}
                                                            />
                                                        )}
                                                    </td>
                                                    <td style={{ ...tdStyle, color: nameColor, fontWeight: "bold", cursor: "pointer" }}
                                                        onClick={() => toggleExpandedProject(p.project_id)}>
                                                        {expandedProjects.has(p.project_id) ? "▼ " : "▶ "}{p.project_name}
                                                        {p.has_subcontractor_pool_hours && (
                                                            <span style={{
                                                                marginLeft: "8px", fontSize: "10px", fontWeight: "bold",
                                                                color: "#996515", backgroundColor: "#FFF3D6",
                                                                border: "1px solid #F0D28A", borderRadius: "10px",
                                                                padding: "2px 8px",
                                                            }}>
                                                                Subcontractor hours
                                                            </span>
                                                        )}
                                                        {p.billing_grouping_is_default && (
                                                            // BACKLOG.md #27f: non-blocking warning that
                                                            // _effective_grouping() had to guess a default
                                                            // because this project's Billing Grouping was
                                                            // never explicitly set -- same badge convention
                                                            // as Subcontractor hours above.
                                                            <span style={{
                                                                marginLeft: "8px", fontSize: "10px", fontWeight: "bold",
                                                                color: "#7F6000", backgroundColor: "#FFF9DB",
                                                                border: "1px solid #E6D488", borderRadius: "10px",
                                                                padding: "2px 8px",
                                                            }}>
                                                                Billing Grouping not set — using default
                                                            </span>
                                                        )}
                                                        {p.blocked && (
                                                            <div style={{ fontSize: "11px", fontWeight: "normal", color: "#C00000" }}>
                                                                {p.blocked_reasons.join("; ")}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={tdStyle}>{p.customer_name || "—"}</td>
                                                    <td style={tdStyle}>{money(p.total)}</td>
                                                </tr>
                                                {expandedProjects.has(p.project_id) && p.line_items.map(li => {
                                                    const lineKey = `${p.project_id}::${li.label}`;
                                                    const savedSplit = li.is_subcontractor_pool ? subcontractorSplits[p.project_id]?.[li.label] : undefined;
                                                    const isEditingSplit = splitEditorKey === lineKey;

                                                    // BACKLOG.md #19: once a split is saved for this pooled line,
                                                    // show the real per-subcontractor rows Pete typed in place of the
                                                    // single anonymous "Amplify Subcontractor" line, with Edit/Clear
                                                    // to revisit it.
                                                    if (savedSplit && savedSplit.length > 0 && !isEditingSplit) {
                                                        const splitTotalHours = Math.round(savedSplit.reduce((s, r) => s + r.hours, 0) * 100) / 100;
                                                        return (
                                                            <React.Fragment key={lineKey}>
                                                                <tr style={{ backgroundColor: "#FFF8EC" }}>
                                                                    <td style={tdStyle}></td>
                                                                    <td style={{ ...tdStyle, paddingLeft: "28px", fontSize: "11px", color: "#996515" }} colSpan={3}>
                                                                        Split from "{li.label}" pooled hours —{" "}
                                                                        <span style={{ cursor: "pointer", textDecoration: "underline" }}
                                                                            onClick={() => openSplitEditor(p.project_id, li.label, splitTotalHours)}>
                                                                            Edit
                                                                        </span>
                                                                        {" | "}
                                                                        <span style={{ cursor: "pointer", textDecoration: "underline" }}
                                                                            onClick={() => clearSplit(p.project_id, li.label)}>
                                                                            Clear
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                {savedSplit.map((row, idx) => (
                                                                    <tr key={`${lineKey}::${idx}`} style={{ backgroundColor: "#FAFAFA" }}>
                                                                        <td style={tdStyle}></td>
                                                                        <td style={{ ...tdStyle, paddingLeft: "40px" }}>
                                                                            {row.name}
                                                                            <span style={{ color: "#999" }}> ({row.hours.toFixed(2)} hrs @ {money(li.rate)})</span>
                                                                        </td>
                                                                        <td style={tdStyle}></td>
                                                                        <td style={tdStyle}>{money(Math.round(row.hours * li.rate * 100) / 100)}</td>
                                                                    </tr>
                                                                ))}
                                                            </React.Fragment>
                                                        );
                                                    }

                                                    return (
                                                        <React.Fragment key={lineKey}>
                                                            <tr style={{ backgroundColor: "#FAFAFA" }}>
                                                                <td style={tdStyle}></td>
                                                                <td style={{ ...tdStyle, paddingLeft: "28px", cursor: "pointer" }}
                                                                    onClick={() => toggleExpandedLine(lineKey)}>
                                                                    {expandedLines.has(lineKey) ? "▼ " : "▶ "}{li.label}
                                                                    <span style={{ color: "#999" }}> ({li.hours.toFixed(2)} hrs @ {money(li.rate)})</span>
                                                                    {li.is_subcontractor_pool && (
                                                                        <button
                                                                            style={{ ...smallBtnStyle, marginLeft: "10px", backgroundColor: "#F18E00" }}
                                                                            onClick={e => { e.stopPropagation(); openSplitEditor(p.project_id, li.label, li.hours); }}
                                                                        >
                                                                            Split by subcontractor
                                                                        </button>
                                                                    )}
                                                                </td>
                                                                <td style={tdStyle}></td>
                                                                <td style={tdStyle}>{money(li.amount)}</td>
                                                            </tr>
                                                            {isEditingSplit && (
                                                                <tr>
                                                                    <td colSpan={4} style={{ ...tdStyle, backgroundColor: "#FFF8EC", paddingLeft: "40px" }}>
                                                                        <div style={{ fontSize: "12px", color: "#5C5D5E", marginBottom: "8px" }}>
                                                                            Type each real subcontractor's name and how many of this line's{" "}
                                                                            {li.hours.toFixed(2)} pooled hours belong to them. Zoho only ever sees this
                                                                            as one shared "{li.label}" account, so this is a manual split — rows must
                                                                            add up to {li.hours.toFixed(2)} exactly before saving.
                                                                        </div>
                                                                        {splitDraftRows.map((row, idx) => (
                                                                            <div key={idx} style={{ display: "flex", gap: "8px", marginBottom: "6px", alignItems: "center" }}>
                                                                                <input
                                                                                    type="text" placeholder="Subcontractor name" value={row.name}
                                                                                    onChange={e => updateSplitRow(idx, "name", e.target.value)}
                                                                                    style={{ padding: "6px 10px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", flex: 1 }}
                                                                                />
                                                                                <input
                                                                                    type="number" placeholder="Hours" value={row.hours} step="0.01"
                                                                                    onChange={e => updateSplitRow(idx, "hours", e.target.value)}
                                                                                    style={{ padding: "6px 10px", fontSize: "13px", border: "1px solid #ddd", borderRadius: "4px", width: "100px" }}
                                                                                />
                                                                                <button
                                                                                    style={dangerBtnStyle}
                                                                                    disabled={splitDraftRows.length <= 1}
                                                                                    onClick={() => removeSplitRow(idx)}
                                                                                >
                                                                                    Remove
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                        <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: "8px" }}>
                                                                            <button style={secondaryBtnStyle} onClick={addSplitRow}>+ Add Subcontractor</button>
                                                                            <span style={{
                                                                                fontSize: "12px",
                                                                                color: Math.abs(splitDraftTotalHours - li.hours) < 0.01 ? "#2E7D32" : "#C00000",
                                                                            }}>
                                                                                {splitDraftTotalHours.toFixed(2)} / {li.hours.toFixed(2)} hrs
                                                                            </span>
                                                                        </div>
                                                                        <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
                                                                            <button
                                                                                style={{
                                                                                    ...primaryBtnStyle,
                                                                                    opacity: (Math.abs(splitDraftTotalHours - li.hours) < 0.01 && splitDraftRows.every(r => r.name.trim())) ? 1 : 0.5,
                                                                                }}
                                                                                disabled={!(Math.abs(splitDraftTotalHours - li.hours) < 0.01 && splitDraftRows.every(r => r.name.trim()))}
                                                                                onClick={() => saveSplitEditor(p.project_id, li.label)}
                                                                            >
                                                                                Save Split
                                                                            </button>
                                                                            <button style={secondaryBtnStyle} onClick={cancelSplitEditor}>Cancel</button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            {expandedLines.has(lineKey) && (
                                                                <tr>
                                                                    <td colSpan={4} style={{ ...tdStyle, backgroundColor: "#FFFFFF", paddingLeft: "40px" }}>
                                                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}></th>
                                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Task</th>
                                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Date</th>
                                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Hours</th>
                                                                                    <th style={{ ...thStyle, backgroundColor: "#8a8b8c" }}>Comments</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {li.entries.map((entry, idx) => (
                                                                                    <tr key={entry.time_entry_id || idx}>
                                                                                        <td style={tdStyle}>
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={!excludedByProject[p.project_id]?.has(entry.time_entry_id || "")}
                                                                                                onChange={() => toggleEntryExcluded(p.project_id, entry.time_entry_id)}
                                                                                            />
                                                                                        </td>
                                                                                        <td style={tdStyle}>{entry.task || "—"}</td>
                                                                                        <td style={tdStyle}>{entry.date || "—"}</td>
                                                                                        <td style={tdStyle}>{entry.hours.toFixed(2)}</td>
                                                                                        <td style={tdStyle}>{entry.comments}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                    <tr>
                                        <td style={tdStyle}></td>
                                        <td style={{ ...tdStyle, fontWeight: "bold" }}>TOTAL</td>
                                        <td style={tdStyle}></td>
                                        <td style={{ ...tdStyle, fontWeight: "bold" }}>{money(computedRunTotal)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div style={{ marginTop: "16px", display: "flex", gap: "10px", alignItems: "center" }}>
                        {/* 2026-09-10, Pete's explicit request: collapsed the old separate
                            Approve-then-Push two-button flow into one -- checking a
                            project's box already means "approve and push this," so
                            there's no reason to require a second confirmation click.
                            See BACKLOG.md #20. */}
                        <button
                            style={{ ...primaryBtnStyle, opacity: pushing || approvedIds.size === 0 ? 0.6 : 1 }}
                            disabled={pushing || approvedIds.size === 0}
                            onClick={handlePush}
                        >
                            {pushing ? "Pushing to Zoho…" : `Push to Zoho (${approvedIds.size})`}
                        </button>
                    </div>
                </div>
            )}

            {step === "summary" && (
                <div style={sectionStyle}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                        <h2 style={{ color: "#5C5D5E", fontSize: "15px", margin: 0 }}>Run Summary</h2>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <button style={secondaryBtnStyle} onClick={refreshSummary} disabled={summaryLoading}>
                                {summaryLoading ? "Refreshing…" : "Refresh"}
                            </button>
                            <button style={secondaryBtnStyle} onClick={() => setStep("start")}>Back to Start</button>
                        </div>
                    </div>

                    {bulkResultMsg && <div style={{ fontSize: "12px", color: "#2E7D32", marginBottom: "10px" }}>{bulkResultMsg}</div>}

                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}></th>
                                    <th style={thStyle}>Project</th>
                                    <th style={thStyle}>Invoice #</th>
                                    <th style={thStyle}>Amount</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>PDF</th>
                                    <th style={thStyle}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {runLines.map(line => (
                                    <tr key={line.id}>
                                        <td style={tdStyle}>
                                            {/* 2026-09-15 (v56): invoice 11098 was emailed twice, so the
                                                checkbox was hidden for "emailed" lines. 2026-10-05 (v81):
                                                that also made Mark as Sent impossible -- the v61 gate
                                                requires "emailed" first, then Mark as Sent needs the
                                                line selected. Checkbox is back for "emailed"; the
                                                double-send protection now lives in handleBulkSendEmail
                                                (drops emailed lines) plus the server-side status guard.
                                                Still hidden for "voided"/"marked_sent". */}
                                            {line.status !== "voided" && line.status !== "marked_sent" && (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLineIds.has(line.id)}
                                                    onChange={() => toggleLineSelected(line.id)}
                                                />
                                            )}
                                        </td>
                                        <td style={tdStyle}>{line.project_name}</td>
                                        <td style={tdStyle}>{line.invoice_number || "—"}</td>
                                        <td style={tdStyle}>{money(line.amount)}</td>
                                        <td style={{ ...tdStyle, color: STATUS_COLORS[line.status] || "#333", fontWeight: "bold" }}>
                                            {STATUS_LABELS[line.status] || line.status}
                                            {line.error_message && (
                                                <div style={{ fontSize: "11px", color: "#C00000", fontWeight: "normal" }}>{line.error_message}</div>
                                            )}
                                        </td>
                                        <td style={tdStyle}>
                                            {line.pdf_sharepoint_url ? (
                                                <a href={line.pdf_sharepoint_url} target="_blank" rel="noreferrer">View</a>
                                            ) : "—"}
                                        </td>
                                        <td style={tdStyle}>
                                            {line.status === "pushed" && line.error_message && (
                                                <button
                                                    style={{ ...secondaryBtnStyle, opacity: retryingLineId === line.id ? 0.6 : 1, marginRight: "6px" }}
                                                    disabled={retryingLineId === line.id}
                                                    onClick={() => handleRetryPdf(line.id)}
                                                >
                                                    {retryingLineId === line.id ? "Retrying…" : "Retry PDF"}
                                                </button>
                                            )}
                                            {line.zoho_invoice_id && line.status !== "voided" && (
                                                <button
                                                    style={{ ...dangerBtnStyle, opacity: voidingLineId === line.id ? 0.6 : 1 }}
                                                    disabled={voidingLineId === line.id}
                                                    onClick={() => handleVoid(line.id)}
                                                >
                                                    {voidingLineId === line.id ? "Voiding…" : "Void"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ marginTop: "16px", display: "flex", gap: "10px" }}>
                        <button
                            style={{ ...secondaryBtnStyle, opacity: bulkBusy || selectedLineIds.size === 0 ? 0.6 : 1 }}
                            disabled={bulkBusy || selectedLineIds.size === 0}
                            onClick={handleBulkSendEmail}
                        >
                            {bulkAction === "email" ? "Sending emails…" : `Send Email (${selectedLineIds.size})`}
                        </button>
                        <button
                            style={{ ...primaryBtnStyle, opacity: bulkBusy || selectedLineIds.size === 0 ? 0.6 : 1 }}
                            disabled={bulkBusy || selectedLineIds.size === 0}
                            onClick={handleBulkMarkSent}
                        >
                            {bulkAction === "marking" ? "Marking as sent…" : `Mark as Sent (${selectedLineIds.size})`}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceAutomation;
